import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  AwarenessEvent,
  getAwarenessEvents,
  deleteAwarenessEvent,
} from "../../../services/Writer/awarenessService";

const AwarenessListPage: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<AwarenessEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<string>("All");
  const [selectedSubtype, setSelectedSubtype] = useState<string>("All");

  const months = [
    "All", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const subtypes = ["All", "Day", "Week", "Month"];

  const filteredRows = rows.filter((row) => {
    const matchesSearch = (row.title || "").toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    if (selectedMonth !== "All") {
      if (!row.start_date) return false;
      const datePart = row.start_date.split('T')[0].split(' ')[0];
      const parts = datePart.split('-');
      if (parts.length >= 2) {
        const monthIndex = parseInt(parts[1], 10) - 1;
        const monthName = months[monthIndex + 1];
        if (monthName !== selectedMonth) return false;
      } else {
        return false;
      }
    }

    if (selectedSubtype !== "All") {
      if ((row.event_subtype || "").toLowerCase() !== selectedSubtype.toLowerCase()) {
        return false;
      }
    }

    return true;
  });

  // -----------------------------
  // Load awareness events
  // -----------------------------
  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await getAwarenessEvents();
      setRows(data);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load awareness events"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // -----------------------------
  // Delete event
  // -----------------------------
  const handleDelete = async (eventId: string) => {
    if (!window.confirm("Delete this awareness event?")) return;

    try {
      await deleteAwarenessEvent(eventId);

      setRows((prev) => prev.filter((row) => row.event_id !== eventId));

      toast.success("Event deleted successfully");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to delete event"
      );
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <ToastContainer position="top-right" autoClose={2500} />

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Awareness Events
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage health days, weeks and awareness campaigns
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* SEARCH */}
          <input
            type="text"
            placeholder="Search awareness..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
          />

          {/* MONTH FILTER */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 border rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {m === "All" ? "All Months" : m}
              </option>
            ))}
          </select>

          {/* SUBTYPE FILTER */}
          <select
            value={selectedSubtype}
            onChange={(e) => setSelectedSubtype(e.target.value)}
            className="px-3 py-2 border rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {subtypes.map((s) => (
              <option key={s} value={s}>
                {s === "All" ? "All Subtypes" : s}
              </option>
            ))}
          </select>

          {/* ADD EVENT */}
          <button
            onClick={() => navigate("/writer/awareness/create")}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Event
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="p-3 text-left">Title</th>
              <th className="p-3 text-left">Start Date</th>
              <th className="p-3 text-left">End Date</th>
              <th className="p-3 text-left">Priority</th>
              <th className="p-3 text-left">Active</th>
              <th className="p-3 text-left">Image</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center p-6">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center p-6 text-gray-500">
                  No awareness events found
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr
                  key={row.event_id}
                  className="border-t border-gray-200 dark:border-gray-700"
                >
                  <td className="p-3 font-medium">{row.title}</td>

                  <td className="p-3">
                    {row.start_date
                      ? new Date(row.start_date).toLocaleDateString()
                      : "-"}
                  </td>

                  <td className="p-3">
                    {row.end_date
                      ? new Date(row.end_date).toLocaleDateString()
                      : "-"}
                  </td>

                  <td className="p-3">{row.priority ?? "-"}</td>

                  <td className="p-3">
                    {row.active ? (
                      <span className="text-green-600 font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="text-gray-400">Inactive</span>
                    )}
                  </td>

                  <td className="p-3">
                    {row.featured_image ? (
                      <img
                        src={row.featured_image}
                        alt="event"
                        className="h-12 w-20 object-cover rounded"
                      />
                    ) : (
                      "-"
                    )}
                  </td>

                  {/* ACTIONS */}
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() =>
                          navigate(`/writer/awareness/view/${row.event_id}`)
                        }
                        className="p-2 rounded bg-gray-700 text-white hover:bg-gray-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          navigate(`/writer/awareness/edit/${row.event_id}`)
                        }
                        className="p-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(row.event_id)}
                        className="p-2 rounded bg-red-600 text-white hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AwarenessListPage;